import {
  VertexData,
  MeshBuilder,
  Mesh,
  Texture,
  DynamicTexture,
  StandardMaterial,
  Color3
} from "@babylonjs/core";
import { closedMillingSolid } from "./millingSolid.js";
import { stretchAxis } from "./millingStretch.js";

const sceneTextures = new WeakMap();

// Smooth the curved cutter profile, but retain sharp shoulders and board corners.
export function creaseNormals(data) {
  const faces = [],
    adjacent = new Map();
  for (let i = 0; i < data.indices.length; i += 3) {
    const ids = data.indices.slice(i, i + 3);
    const p = ids.map((id) => data.positions.slice(id * 3, id * 3 + 3));
    const a = p[0].map((n, k) => n - p[1][k]);
    const b = p[2].map((n, k) => n - p[1][k]);
    const normal = [
      a[1] * b[2] - a[2] * b[1],
      a[2] * b[0] - a[0] * b[2],
      a[0] * b[1] - a[1] * b[0]
    ];
    const length = Math.hypot(...normal) || 1;
    const face = { ids, normal: normal.map((n) => n / length), area: length };
    faces.push(face);
    ids.forEach((id) => {
      if (!adjacent.has(id)) adjacent.set(id, []);
      adjacent.get(id).push(face);
    });
  }
  const positions = [],
    normals = [],
    indices = [],
    uvs = [];
  const cache = new Map();
  faces.forEach((face) =>
    face.ids.forEach((id) => {
      const sum = [0, 0, 0];
      adjacent.get(id).forEach((other) => {
        if (
          face.normal.reduce((dot, n, k) => dot + n * other.normal[k], 0) >=
          Math.cos(Math.PI / 4)
        ) {
          other.normal.forEach((n, k) => {
            sum[k] += n * other.area;
          });
        }
      });
      const length = Math.hypot(...sum) || 1;
      const normal = sum.map((n) => n / length);
      const key = `${id}:${normal.map((n) => n.toFixed(5)).join(",")}`;
      if (!cache.has(key)) {
        cache.set(key, positions.length / 3);
        positions.push(...data.positions.slice(id * 3, id * 3 + 3));
        normals.push(...normal);
        uvs.push(...data.uvs.slice(id * 2, id * 2 + 2));
      }
      indices.push(cache.get(key));
    })
  );
  Object.assign(data, { positions, normals, indices, uvs });
}
function textureFor(scene, key, asset) {
  let cache = sceneTextures.get(scene);
  if (!cache) {
    cache = new Map();
    sceneTextures.set(scene, cache);
    scene.onDisposeObservable.addOnce(() => {
      cache.forEach((t) => t.dispose());
      cache.clear();
    });
  }
  if (cache.has(key)) return cache.get(key);
  let texture;
  if (asset.kind === "lines") {
    texture = new DynamicTexture(`milling_${key}`, 1024, scene, false);
    const ctx = texture.getContext();
    ctx.clearRect(0, 0, 1024, 1024);
    ctx.strokeStyle = "#444";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    asset.lines.forEach(([x, y, u, v]) => {
      ctx.moveTo(x * 1024, (1 - y) * 1024);
      ctx.lineTo(u * 1024, (1 - v) * 1024);
    });
    ctx.stroke();
    texture.hasAlpha = true;
    texture.update();
  } else {
    texture = new Texture(
      asset.kind === "normal"
        ? `data:image/png;base64,${asset.normalMap}`
        : asset.image,
      scene
    );
    texture.hasAlpha = asset.kind === "image" || asset.kind === "shade";
    if (asset.kind === "normal") texture.gammaSpace = false;
  }
  cache.set(key, texture);
  return texture;
}

export function createMillingDisplay(mesh, { w, h, d, widthMm, heightMm, swapAxes=false }) {
  // Item meshes use local X=height, Y=width. Work in the milling's own
  // width/height coordinates, then rotate into the existing board axes.
  const millingW=swapAxes?h:w, millingH=swapAxes?w:h;
  const original = VertexData.ExtractFromMesh(mesh, true, true);
  const scene = mesh.getScene();
  let overlay,
    material,
    detailed = false;
  let baseMaterial;
  let normalOverlay = false;
  const clearOverlay = () => {
    if (overlay) overlay.dispose();
    if (material) material.dispose(false, false);
    overlay = material = null;
  };
  const visibility = scene.onBeforeRenderObservable.add(() => {
    if (overlay) {
      overlay.isVisible = mesh.isVisible;
      if (normalOverlay && mesh.material) {
        material.diffuseColor.copyFrom(mesh.material.diffuseColor);
        material.diffuseTexture =
          mesh.material.diffuseTexture || mesh.material.emissiveTexture;
      }
    }
  });
  const restore = () => {
    if (detailed && !mesh.isDisposed()) {
      original.applyToMesh(mesh);
      mesh.enableEdgesRendering();
      detailed = false;
      mesh.metadata = { ...mesh.metadata, isMillingSolid: false };
      mesh.showBoundingBox = false;
      mesh.material = baseMaterial;
    }
  };
  return {
    flat(asset, key) {
      restore();
      clearOverlay();
      // Validate before creating an overlay so an impossible shrink leaves the board visible.
      const hasSize =
        asset.size?.[0] > 0 &&
        asset.size?.[1] > 0 &&
        widthMm > 0 &&
        heightMm > 0;
      const xs = hasSize
        ? stretchAxis(asset.size[0], widthMm, 2, asset.stretchX, asset.repeatX)
        : null;
      const ys = hasSize
        ? stretchAxis(asset.size[1], heightMm, 2, asset.stretchY, asset.repeatY)
        : null;
      normalOverlay = asset.kind === "normal";
      material =
        asset.kind === "shade"
          ? new StandardMaterial(`milling_material_${mesh.name}`, scene)
          : mesh.material.clone(`milling_material_${mesh.name}`);
      material.disableLighting = false;
      if (material.emissiveTexture) {
        material.diffuseTexture = material.emissiveTexture;
        material.emissiveTexture = null;
      }
      material.emissiveColor = Color3.Black();
      material.specularColor = new Color3(0.15, 0.15, 0.15);
      const texture = textureFor(scene, key, asset);
      if (asset.kind === "normal") material.bumpTexture = texture;
      else if (asset.kind === "shade") {
        material.diffuseTexture = texture;
        material.emissiveTexture = null;
        material.bumpTexture = null;
        material.opacityTexture = null;
        material.diffuseColor = Color3.Black();
        material.emissiveColor = Color3.Black();
        material.specularColor = Color3.Black();
        material.disableLighting = true;
        material.useAlphaFromDiffuseTexture = true;
        material.alpha = 1;
        material.transparencyMode = 2;
      } else {
        material.diffuseTexture = texture;
        material.useAlphaFromDiffuseTexture = true;
        material.diffuseColor = Color3.White();
      }
      overlay = MeshBuilder.CreatePlane(
        `milling_${mesh.name}`,
        { width: millingW, height: millingH, sideOrientation: Mesh.BACKSIDE },
        scene
      );
      if (hasSize) {
        // Keep the tangent basis invertible for normal mapping in the inserted strip.
        for (const axis of [xs, ys])
          for (let i = 1; i < axis.length; i++) {
            if (
              axis[i].source === axis[i - 1].source &&
              axis[i].target > axis[i - 1].target
            ) {
              axis[i - 1].source -= 1e-7;
              axis[i].source += 1e-7;
            }
          }
        const plane = new VertexData();
        plane.positions = [];
        plane.uvs = [];
        plane.normals = [];
        plane.indices = [];
        ys.forEach((y) =>
          xs.forEach((x) => {
            plane.positions.push((x.target - 0.5) * millingW, (y.target - 0.5) * millingH, 0);
            plane.uvs.push(x.source, y.source);
            plane.normals.push(0, 0, 1);
          })
        );
        for (let y = 0; y < ys.length - 1; y++)
          for (let x = 0; x < xs.length - 1; x++) {
            const a = y * xs.length + x,
              b = a + 1,
              c = a + xs.length,
              d = c + 1;
            plane.indices.push(a, d, b, a, c, d);
          }
        plane.applyToMesh(overlay);
      }
      overlay.parent = mesh;
      overlay.rotation.z=swapAxes?-Math.PI/2:0;
      overlay.position.z = d / 2 + Math.min(w, h, d) * 0.001;
      overlay.material = material;
      overlay.isPickable = false;
      overlay.isVisible = mesh.isVisible;
      overlay.metadata = { isMillingOverlay: true };
    },
    solid(asset) {
      restore();
      clearOverlay();
      asset = closedMillingSolid(
        asset,
        256,
        widthMm > 0 && heightMm > 0 && asset.size
          ? [widthMm, heightMm]
          : undefined
      );
      const data = new VertexData();
      data.positions = [];
      for(let i=0;i<asset.positions.length;i+=3) {
        const x=(asset.positions[i]-.5)*millingW,y=(asset.positions[i+1]-.5)*millingH,z=(asset.positions[i+2]-.5)*d;
        data.positions.push(...(swapAxes?[y,-x,z]:[x,y,z]));
      }
      // DXF uses counterclockwise faces; Babylon's default scene is left handed.
      data.indices = [];
      for (let i = 0; i < asset.indices.length; i += 3)
        data.indices.push(
          asset.indices[i],
          asset.indices[i + 2],
          asset.indices[i + 1]
        );
      data.uvs = [];
      for (let i = 0; i < asset.positions.length; i += 3)
        data.uvs.push(asset.positions[i], asset.positions[i + 1]);
      creaseNormals(data);
      data.applyToMesh(mesh);
      mesh.disableEdgesRendering();
      detailed = true;
      mesh.metadata = { ...mesh.metadata, isMillingSolid: true };
      mesh.renderOutline = false;
      baseMaterial = mesh.material;
      material = baseMaterial.clone(`milling_solid_${mesh.name}`);
      material.disableLighting = false;
      if (material.emissiveTexture) {
        material.diffuseTexture = material.emissiveTexture;
        material.emissiveTexture = null;
      }
      material.emissiveColor = Color3.Black();
      material.specularColor = new Color3(0.12, 0.12, 0.12);
      material.specularPower = 48;
      material.alpha = 1;
      material.transparencyMode = 0;
      material.opacityTexture = null;
      material.useAlphaFromDiffuseTexture = false;
      material.backFaceCulling = true;
      mesh.material = material;
    },
    reset() {
      restore();
      clearOverlay();
    },
    dispose() {
      clearOverlay();
      scene.onBeforeRenderObservable.remove(visibility);
    }
  };
}
